import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notifyError } from '../lib/notify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZE = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
const BTN_PAD = { sm: 'p-1.5', md: 'p-2', lg: 'p-2.5' };

// Reusable heart toggle to save/unsave a favorite provider.
// - Guests are redirected to log in.
// - Optimistic UI with revert on failure.
export const FavoriteButton = ({
  providerId,
  user,
  initialFavorited = false,
  onChange,
  size = 'md',
  variant = 'plain', // 'plain' | 'floating' | 'labeled'
  className = '',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [favorited, setFavorited] = useState(initialFavorited);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setFavorited(initialFavorited);
  }, [initialFavorited]);

  const toggle = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!user) {
      navigate('/login');
      return;
    }
    if (busy || !providerId) return;
    const next = !favorited;
    setBusy(true);
    setFavorited(next); // optimistic
    try {
      if (next) {
        await axios.post(`${API}/favorites/${providerId}`);
      } else {
        await axios.delete(`${API}/favorites/${providerId}`);
      }
      onChange?.(providerId, next);
    } catch (err) {
      setFavorited(!next); // revert
      notifyError(err, 'Could not update favorite');
    } finally {
      setBusy(false);
    }
  };

  const floating =
    variant === 'floating'
      ? 'bg-white/90 backdrop-blur shadow-sm hover:bg-white'
      : 'hover:bg-red-50';

  if (variant === 'labeled') {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-pressed={favorited}
        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        data-testid={`favorite-btn-${providerId}`}
        className={`px-6 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition whitespace-nowrap border disabled:opacity-60 ${
          favorited
            ? 'bg-red-50 border-red-200 text-red-600'
            : 'bg-white border-gray-300 text-gray-700 hover:border-red-300 hover:text-red-500'
        } ${className}`}
      >
        <Heart className={`${SIZE[size]} transition-colors ${favorited ? 'fill-red-500 text-red-500' : ''}`} />
        {favorited ? t('favorites.saved') : t('favorites.save')}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={favorited}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      title={favorited ? 'Remove from favorites' : 'Save to favorites'}
      data-testid={`favorite-btn-${providerId}`}
      className={`inline-flex items-center justify-center rounded-full transition disabled:opacity-60 ${BTN_PAD[size]} ${floating} ${className}`}
    >
      <Heart
        className={`${SIZE[size]} transition-colors ${
          favorited ? 'fill-red-500 text-red-500' : 'text-gray-400'
        }`}
      />
    </button>
  );
};

export default FavoriteButton;
